import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import React, {useContext, useEffect, useMemo, useState} from "react";
// Make sure this path is correct for your project
// import {PAContext} from "../../Services/PAContext";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {DateCalendar, DayCalendarSkeleton, PickersDay} from "@mui/x-date-pickers";
import {Stack} from "@mui/system";

import dayjs from "dayjs";
import weekOfYear from 'dayjs/plugin/weekOfYear';
import {getAllEventsFromDatabase} from "../../Services/BrowserDatabaseFunctions";
import {PAContext} from "../../Services/PAContext";
// Ensure dayjs is extended at the top level of your file or project setup
dayjs.extend(weekOfYear);


// ------------------------------------
// 🛠️ Holiday Calculation Utilities
// ------------------------------------

// Helper function to calculate the floating date
const calculateFloatingHoliday = (rule, year) => {
    const { month, week, dayOfWeek } = rule;
    // Start with the 1st of the target month and year
    let date = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);

    if (week === -1) {
        // Find the Last occurrence of dayOfWeek in the month
        date = date.endOf('month');
        // Subtract days until we hit the target dayOfWeek
        while (date.day() !== dayOfWeek) {
            date = date.subtract(1, 'day');
        }
    } else {
        // Find the Nth occurrence (1st, 2nd, 3rd, 4th)
        // Find the first occurrence of the dayOfWeek in the month
        while (date.day() !== dayOfWeek) {
            date = date.add(1, 'day');
        }
        // Move to the target week: add (week - 1) * 7 days
        date = date.add((week - 1) * 7, 'day');
    }
    return date;
};

// Main function to get all calculated holidays for a year
const getHolidaysForYear = (rawHolidays, year) => {
    return rawHolidays.map(holiday => {
        let date;

        if (holiday.date) {
            // Fixed date
            date = dayjs(`${year}-${holiday.date}`);
        } else if (holiday.rule) {
            // Dynamic date
            date = calculateFloatingHoliday(holiday.rule, year);
        } else {
            return null;
        }

        return {
            date,
            name: holiday.name,
            countries: holiday.countries,
            // Key for fast lookup in the calendar
            dateKey: date.format('YYYY-MM-DD')
        };
    }).filter(h => h !== null);
};


// ------------------------------------
// 🌍 Revised Global Holiday Data
// ------------------------------------

// This array now uses 'rule' for floating holidays and 'date' for fixed holidays.
const countryHolidays = [
    // Fixed Date Holidays
    {"date": "01-01", "name": "New Year's Day", "countries": ["USA", "CAN", "UK", "FRA", "GER", "BRA", "SAF", "IND", "JAP"]},
    {"date": "07-04", "name": "Independence Day", "countries": ["USA"]},
    {"date": "11-11", "name": "Veterans Day", "countries": ["USA", "CAN", "FRA", "UK", "SAF"]},
    {"date": "12-25", "name": "Christmas Day", "countries": ["USA", "CAN", "UK", "FRA", "GER", "BRA", "SAF", "IND", "JAP"]},
    {"date": "12-26", "name": "Boxing Day", "countries": ["UK", "CAN", "SAF"]},
    {"date": "07-01", "name": "Canada Day", "countries": ["CAN"]},
    {"date": "05-01", "name": "Labour Day", "countries": ["FRA", "GER", "BRA", "SAF"]},
    {"date": "05-08", "name": "Victory in Europe Day", "countries": ["FRA"]},
    {"date": "07-14", "name": "Bastille Day", "countries": ["FRA"]},
    {"date": "08-15", "name": "Assumption Day", "countries": ["FRA"]},
    {"date": "11-01", "name": "All Saints' Day", "countries": ["FRA"]},
    {"date": "10-03", "name": "Day of German Unity", "countries": ["GER"]},
    {"date": "04-21", "name": "Tiradentes Day", "countries": ["BRA"]},
    {"date": "09-07", "name": "Independence Day (Brazil)", "countries": ["BRA"]},
    {"date": "10-12", "name": "Our Lady of Aparecida", "countries": ["BRA"]},
    {"date": "11-02", "name": "All Souls' Day", "countries": ["BRA"]},
    {"date": "11-15", "name": "Republic Proclamation Day", "countries": ["BRA"]},
    {"date": "03-21", "name": "Human Rights Day", "countries": ["SAF"]},
    {"date": "04-27", "name": "Freedom Day", "countries": ["SAF"]},
    {"date": "06-16", "name": "Youth Day", "countries": ["SAF"]},
    {"date": "08-09", "name": "National Women's Day", "countries": ["SAF"]},
    {"date": "09-24", "name": "Heritage Day", "countries": ["SAF"]},
    {"date": "12-16", "name": "Day of Reconciliation", "countries": ["SAF"]},
    {"date": "01-26", "name": "Republic Day", "countries": ["IND"]},
    {"date": "08-15", "name": "Independence Day (India)", "countries": ["IND"]},
    {"date": "10-02", "name": "Gandhi Jayanti", "countries": ["IND"]},
    {"date": "02-11", "name": "National Foundation Day", "countries": ["JAP"]},
    {"date": "02-23", "name": "Emperor's Birthday", "countries": ["JAP"]},
    {"date": "03-20", "name": "Vernal Equinox Day", "countries": ["JAP"]},
    {"date": "04-29", "name": "Showa Day", "countries": ["JAP"]},
    {"date": "05-03", "name": "Constitution Memorial Day", "countries": ["JAP"]},
    {"date": "05-04", "name": "Greenery Day", "countries": ["JAP"]},
    {"date": "05-05", "name": "Children's Day", "countries": ["JAP"]},
    {"date": "08-11", "name": "Mountain Day", "countries": ["JAP"]},
    {"date": "09-23", "name": "Autumnal Equinox Day", "countries": ["JAP"]},
    {"date": "11-03", "name": "Culture Day", "countries": ["JAP"]},
    {"date": "11-23", "name": "Labour Thanksgiving Day", "countries": ["JAP"]},

    // 🗓️ Dynamic Holidays (Floating by day-of-week rule)
    // Rule format: { month: 1-12, week: 1-4 or -1 (Last), dayOfWeek: 0-6 (0=Sun, 1=Mon) }

    // USA 🇺🇸
    {"rule": {"month": 1, "week": 3, "dayOfWeek": 1}, "name": "Martin Luther King, Jr. Day", "countries": ["USA"]},
    {"rule": {"month": 2, "week": 3, "dayOfWeek": 1}, "name": "Presidents' Day", "countries": ["USA"]},
    {"rule": {"month": 5, "week": -1, "dayOfWeek": 1}, "name": "Memorial Day", "countries": ["USA"]}, // Last Monday in May
    {"rule": {"month": 9, "week": 1, "dayOfWeek": 1}, "name": "Labor Day", "countries": ["USA", "CAN"]}, // First Monday in September
    {"rule": {"month": 10, "week": 2, "dayOfWeek": 1}, "name": "Columbus Day", "countries": ["USA"]}, // Second Monday in October
    {"rule": {"month": 11, "week": 4, "dayOfWeek": 4}, "name": "Thanksgiving Day", "countries": ["USA"]}, // Fourth Thursday in November

    // CANADA 🇨🇦
    {"rule": {"month": 2, "week": 3, "dayOfWeek": 1}, "name": "Family Day", "countries": ["CAN"]}, // Third Monday in Feb (Ontario)
    {"rule": {"month": 10, "week": 2, "dayOfWeek": 1}, "name": "Thanksgiving (Canada)", "countries": ["CAN"]}, // Second Monday in October

    // UK 🇬🇧
    {"rule": {"month": 5, "week": 1, "dayOfWeek": 1}, "name": "Early May Bank Holiday", "countries": ["UK"]}, // First Monday in May
    {"rule": {"month": 5, "week": -1, "dayOfWeek": 1}, "name": "Spring Bank Holiday", "countries": ["UK"]}, // Last Monday in May
    {"rule": {"month": 8, "week": -1, "dayOfWeek": 1}, "name": "Summer Bank Holiday", "countries": ["UK"]}, // Last Monday in August

    // JAPAN 🇯🇵
    {"rule": {"month": 1, "week": 2, "dayOfWeek": 1}, "name": "Coming of Age Day", "countries": ["JAP"]}, // Second Monday in Jan
    {"rule": {"month": 7, "week": 3, "dayOfWeek": 1}, "name": "Marine Day", "countries": ["JAP"]}, // Third Monday in July
    {"rule": {"month": 9, "week": 3, "dayOfWeek": 1}, "name": "Respect for the Aged Day", "countries": ["JAP"]}, // Third Monday in Sept
    {"rule": {"month": 10, "week": 2, "dayOfWeek": 1}, "name": "Sports Day", "countries": ["JAP"]}, // Second Monday in Oct
];

const countryCodeMap = {
    USA: {
        name: "United States",
        flag: "https://www.flagcdn.com/20x15/us.png"
    },
    CAN: {
        name: "Canada",
        flag: "https://www.flagcdn.com/20x15/ca.png"
    },
    UK: {
        name: "United Kingdom",
        flag: "https://www.flagcdn.com/20x15/gb.png"
    },
    FRA: {
        name: "France",
        flag: "https://www.flagcdn.com/20x15/fr.png"
    },
    GER: {
        name: "Germany",
        flag: "https://www.flagcdn.com/20x15/de.png"
    },
    BRA: {
        name: "Brazil",
        flag: "https://www.flagcdn.com/20x15/br.png"
    },
    SAF: {
        name: "South Africa",
        flag: "https://www.flagcdn.com/20x15/za.png"
    },
    IND: {
        name: "India",
        flag: "https://www.flagcdn.com/20x15/in.png"
    },
    JAP: {
        name: "Japan",
        flag: "https://www.flagcdn.com/20x15/jp.png"
    }
};


// ------------------------------------
// ⚛️ MasterCalendar Component
// ------------------------------------

function MasterCalendar({ isOpen, onClose }){
    // NOTE: Replace PAContext with your actual context import if needed

    const { dataArray } = useContext(PAContext);
    const { Account, authenticated } = dataArray;

    const [ allEvents , setAllEvents ] = useState([]);
    const [ eventDates, setEventDates ] = useState([]);
    const [ eventCountByYear , setEventCountByYear ] = useState([]);
    const [ eventCount12Months , setEventCount12Months ] = useState([]);
    const [ dateMarkersMap, setDateMarkersMap ] = useState({});
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedHoliday, setSelectedHoliday] = useState(null);

    // 💡 NEW STATE: Tracks the currently displayed month/year for holiday calculation
    const [currentDate, setCurrentDate] = useState(dayjs());
    // 💡 NEW STATE: Stores the dynamically calculated holidays for the current year
    const [calculatedHolidays, setCalculatedHolidays] = useState([]);


    // 🛑 NEW EFFECT: Calculate holidays whenever the year changes
    useEffect(() => {
        const year = currentDate.year();
        const holidays = getHolidaysForYear(countryHolidays, year);
        setCalculatedHolidays(holidays);
    }, [currentDate]);

    // 💡 NEW MEMO: Map for O(1) holiday lookup
    const calculatedHolidaysMap = useMemo(() => {
        return calculatedHolidays.reduce((acc, h) => {
            acc[h.dateKey] = h; // Map: 'YYYY-MM-DD' -> holiday object
            return acc;
        }, {});
    }, [calculatedHolidays]);


    useEffect(() => {
        //pull all the Events
        if (authenticated){
            console.log("IS logged in so checking for Events!!");
            getAllEventsFromDatabase(Account.id).then((_events) => {
                setAllEvents(_events);
            });
        } else {
            console.log("not logged in so no checking for Events!!");
        }
        // NOTE: If dataArray.Events is the source, it should be set here.
        // Assuming setAllEvents(dataArray.Events) happens elsewhere or is mocked here.
    }, [authenticated]);

    useEffect(() => {
        if (!Array.isArray(allEvents)) return;

        const dateSet = new Set(); // For unique event dates
        const eventsByYear = {};
        const next12MonthsEvents = [];
        const markersByDate = {};

        const today = dayjs();
        const twelveMonthsFromNow = today.add(12, 'month');

        const addMarker = (dateStr, type) => {
            if (!markersByDate[dateStr]) {
                markersByDate[dateStr] = new Set();
            }
            markersByDate[dateStr].add(type);
        };

        allEvents.forEach((_event) => {
            // Event Dates
            _event?.time_table?.dates?.forEach(({ date }) => {
                if (date) {
                    const formatted = dayjs(date).format('YYYY-MM-DD');
                    dateSet.add(formatted);

                    const year = dayjs(date).year();
                    eventsByYear[year] = (eventsByYear[year] || 0) + 1;
                }
            });

            // 12-Month Filter
            const firstDate = dayjs(_event?.time_table?.dates?.[0]?.date);
            const lastDate = dayjs(_event?.time_table?.dates?.slice(-1)[0]?.date);

            if (
                firstDate.isValid() &&
                firstDate.isBefore(twelveMonthsFromNow) &&
                lastDate.isAfter(today)
            ) {
                next12MonthsEvents.push(_event);
            }

            // Add markers per category
            const collectMarkers = (list, type) => {
                list?.forEach(item => {
                    item?.time_table?.dates?.forEach(({ date }) => {
                        if (date) {
                            const d = dayjs(date).format('YYYY-MM-DD');
                            addMarker(d, type);
                        }
                    });
                });
            };

            collectMarkers(_event.meals, "meal");
            collectMarkers(_event.lodging, "lodging");
            collectMarkers(_event.activities, "activity");
        });

        // Convert marker sets to arrays
        const finalMarkerMap = {};
        Object.entries(markersByDate).forEach(([date, set]) => {
            finalMarkerMap[date] = Array.from(set);
        });

        // Set state with aggregated results
        setEventDates(Array.from(dateSet).map(dateStr => dayjs(dateStr)));
        setEventCountByYear(eventsByYear);
        setEventCount12Months(next12MonthsEvents);
        setDateMarkersMap(finalMarkerMap);
    }, [allEvents]);

    const handleDayClick = (date) => {
        const dateStr = date.format("YYYY-MM-DD");
        setSelectedDay(date);

        // 🛑 FIX: Look up holiday using the calculated map (YYYY-MM-DD)
        const holiday = calculatedHolidaysMap[dateStr];
        setSelectedHoliday(holiday || null);

        // Check for event (ensure event date comparison is robust)
        const matchingEvent = allEvents.find(event =>
            event.time_table?.dates?.some(d => dayjs(d.date).isSame(date, 'day'))
        );
        setSelectedEvent(matchingEvent || null);
    };

    function getMarkersForDate(date) {
        const key = dayjs(date).format("YYYY-MM-DD");
        return dateMarkersMap[key] || [];
    }
    function isHighlighted(date) {
        // Check if the provided date exists in the selectedDates array
        return eventDates.some(selectedDate => date.isSame(selectedDate, 'day'));
    }

    function ServerDay(props) {
        const { day, outsideCurrentMonth, eventData, ...other } = props;

        const isSelected = !outsideCurrentMonth && isHighlighted(dayjs(day));

        // 🛑 FIX: Use YYYY-MM-DD to look up the calculated holiday
        const dateStr = day.format("YYYY-MM-DD");
        const matchedHoliday = calculatedHolidaysMap[dateStr];
        const isHoliday = Boolean(matchedHoliday);

        const markers = getMarkersForDate(dayjs(day)); // Event markers

        const holidayCountries = matchedHoliday?.countries || [];

        return (
            <PickersDay
                {...other}
                outsideCurrentMonth={outsideCurrentMonth}
                day={day}
                onClick={() => handleDayClick(dayjs(day))}
                sx={{
                    ...(isSelected
                        ? {
                            backgroundColor: "#1976d2",
                            color: "white",
                            borderRadius: "50%",
                            "&:hover": {
                                backgroundColor: "#115293",
                            },
                        }
                        : {
                            color: outsideCurrentMonth ? "#ccc" : "inherit",
                        }),
                    // Apply holiday styling based on the calculated result
                    ...(isHoliday && isSelected && {
                        border: "2px solid #FFD700",
                        boxSizing: "border-box",
                    }),
                    ...(isHoliday && !isSelected && {
                        backgroundColor: "#fef4ec",
                        borderRadius: "50%",
                    }),
                    position: "relative",
                }}
            >
                <span>{day.format("D")}</span>

                {/* Markers (meals/lodging/activities) */}
                {markers.length > 0 && (
                    <div
                        style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            display: "flex",
                            gap: "3px",
                        }}
                    >
                        {markers.map((marker, index) => (
                            <span
                                key={index}
                                style={{
                                    width: "5px",
                                    height: "5px",
                                    borderRadius: "50%",
                                    backgroundColor:
                                        marker === "meal"
                                            ? "#FF6347"
                                            : marker === "lodging"
                                                ? "#32CD32"
                                                : "#FFD700",
                                }}
                            ></span>
                        ))}
                    </div>
                )}

                {/* Flag icon (bottom left) */}
                {isHoliday && holidayCountries.length === 1 && (
                    <img
                        src={countryCodeMap[holidayCountries[0]]?.flag}
                        alt={holidayCountries[0]}
                        style={{
                            position: "absolute",
                            bottom: 2,
                            left: 2,
                            width: 15,
                            height: 10,
                            borderRadius: 2,
                        }}
                    />
                )}

                {isHoliday && holidayCountries.length > 1 && (
                    <span
                        title={holidayCountries.map((code) => countryCodeMap[code]?.name).join(", ")}
                        style={{
                            position: "absolute",
                            bottom: 2,
                            left: 2,
                            fontSize: "10px",
                            color: "#FFD700",
                        }}
                    >
          🏳️
        </span>
                )}
            </PickersDay>
        );
    }

    // --- Message Tiers Configuration ---
    const totalEventsMessages = useMemo(() => [
        { threshold: 0, getMessage: () => "Ready to embark on your first grand adventure?" },
        { threshold: 1, getMessage: (count) => `You've conquered ${count} exciting adventure${count === 1 ? '' : 's'}!` },
        { threshold: 5, getMessage: (count) => `You're building quite the adventure legacy with ${count} journeys!` },
        { threshold: 10, getMessage: (count) => `A true trailblazer! You've successfully conquered ${count} epic adventures!` },
        { threshold: 20, getMessage: (count) => `Legendary adventurer! Your ${count} incredible journeys inspire us all!` },
    ], []);
    const upcomingEventsMessages = useMemo(() => [
        { threshold: 0, getMessage: () => "The next 12 months are a blank canvas, ready for your plans!" },
        { threshold: 1, getMessage: (count) => `And there's ${count} more exciting journey${count === 1 ? '' : 's'} waiting for you soon!` },
        { threshold: 3, getMessage: (count) => `Get ready! ${count} more adventures are on the horizon in the next 12 months!` },
        { threshold: 6, getMessage: (count) => `Your next year is packed with ${count} thrilling journeys to anticipate!` },
        { threshold: 10, getMessage: (count) => `Wow! ${count} grand expeditions are set to unfold in the coming year!` },
    ], []);
    // Helper to get the appropriate message
    const getMessageForCount = (count, messageTiers) => {
        const sortedTiers = [...messageTiers].sort((a, b) => b.threshold - a.threshold);
        for (let tier of sortedTiers) {
            if (count >= tier.threshold) {
                return tier.getMessage(count);
            }
        }
        return messageTiers[0].getMessage(count); // Fallback to the lowest threshold message
    };

    return (

        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Adventure Calendar</DialogTitle>
            <DialogContent>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(145deg, #a7f3d0 0%, #a5b4fc 100%)',
                        borderRadius: '16px',
                        p: { xs: 2.5, md: 3.5 },
                        mb: 3,
                        mt: 2,
                        boxShadow: '0 6px 15px rgba(0,0,0,0.1)',
                        textAlign: 'center',
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 12px 25px rgba(0,0,0,0.15)',
                        }
                    }}
                >
                    <Typography variant="h5" fontWeight="bold" color="#333" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className='icon-trophy' style={{ fontSize: '2.5rem', color: '#f5b042' }} />
                        {getMessageForCount(allEvents.length, totalEventsMessages)}
                        <i className='icon-trophy' style={{ fontSize: '2.5rem', color: '#f5b042', transform: 'scaleX(-1)' }} />
                    </Typography>

                    <Typography variant="body1" color="#555" sx={{ mb: 2 }}>
                        {getMessageForCount(eventCount12Months.length, upcomingEventsMessages)}
                    </Typography>


                    <Button
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(45deg, #FF8A65 30%, #FFB74D 90%)',
                            color: 'white',
                            fontWeight: 700,
                            borderRadius: '25px',
                            px: 4,
                            py: 1.5,
                            boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 5px 10px 3px rgba(255, 105, 135, .5)',
                            }
                        }}
                        onClick={() => {
                            console.log("Plan Your Next Adventure button clicked!");
                            // In a real app, this would navigate to the event creation page
                        }}
                    >
                        🗺️ Plan Your Next Grand Adventure!
                    </Button>
                </Box>

                {/* now show the Calendar */}
                <Box
                    sx={{
                        backgroundColor: '#fafafa',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.05)'
                    }}
                >
                    <DateCalendar
                        renderLoading={() => <DayCalendarSkeleton />}
                        showDaysOutsideCurrentMonth
                        slots={{ day: ServerDay }}
                        slotProps={{ day: { eventData: allEvents } }}
                        // 🛑 FIX: Add view change handlers to update currentDate state for calculation
                        onMonthChange={(newDate) => setCurrentDate(newDate)}
                        onYearChange={(newDate) => setCurrentDate(newDate)}
                    />
                </Box>

                {/* show the legend */}
                <Box display="flex" justifyContent="center" gap={3} mt={1}>
                    {[
                        { label: 'Meals', color: '#FF6347', emoji: '🍽️' },
                        { label: 'Lodging', color: '#32CD32', emoji: '🛏️' },
                        { label: 'Activities', color: '#FFD700', emoji: '🎉' },
                    ].map(({ label, color, emoji }) => (
                        <Box key={label} display="flex" alignItems="center" gap={0.5}>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                }}
                            />
                            <Typography variant="caption">{emoji} {label}</Typography>
                        </Box>
                    ))}
                </Box>

                {selectedDay && (
                    <Box mt={3} p={2} borderRadius="8px" sx={{ backgroundColor: "#f9f9f9", border: "1px solid #ddd" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {selectedDay.format("dddd, MMMM D, YYYY")}
                        </Typography>

                        {/* Event Details */}
                        {selectedEvent && (
                            <Box display="flex" gap={2} alignItems="flex-start" mt={1}>
                                <Box
                                    component="img"
                                    src={selectedEvent.cover_image_full_data || selectedEvent.cover_image_thumbnail}
                                    alt={selectedEvent.title}
                                    sx={{ width: 80, height: 80, objectFit: "cover", borderRadius: "8px" }}
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedEvent.title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedEvent.location?.formatted_address || "Event Details Available"}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Holiday Details (only if no event) */}
                        {!selectedEvent && selectedHoliday && (
                            <Box mt={1}>
                                {/* Holiday Name */}
                                <Typography variant="body1" sx={{ fontWeight: 500, color: "#D32F2F" }}>
                                    🎉 {selectedHoliday.name}
                                </Typography>

                                {/* Country flags and names */}
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mt={0.5}>
                                    {selectedHoliday.countries.map((code) => (
                                        <Box key={code} display="flex" alignItems="center" gap={0.5}>
                                            <img
                                                src={countryCodeMap[code]?.flag}
                                                alt={code}
                                                width={16}
                                                height={12}
                                                style={{ borderRadius: 2 }}
                                            />
                                            <Typography variant="caption">
                                                {countryCodeMap[code]?.name || code}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}


                        {/* If nothing found */}
                        {!selectedEvent && !selectedHoliday && (
                            <Typography variant="body2" color="text.secondary">
                                No event or holiday for this day.
                            </Typography>
                        )}
                    </Box>
                )}

            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="secondary">Close</Button>
            </DialogActions>
        </Dialog>

    )

}

export default MasterCalendar;